import { z } from "zod";

const nonEmptyString = z.string().trim().min(1);

const httpUrl = z
  .string()
  .url()
  .refine((value) => value.startsWith("http://") || value.startsWith("https://"), {
    message: "must be a valid HTTP/HTTPS URL",
  });

const wsUrl = z
  .string()
  .url()
  .refine((value) => value.startsWith("ws://") || value.startsWith("wss://"), {
    message: "must be a valid WS/WSS URL",
  });

const booleanString = z.enum(["true", "false"]).transform((value) => value === "true");

const clientEnvSchema = z.object({
  VITE_API_URL: httpUrl,
  // Clerk is the sole identity provider — the app cannot authenticate without it
  VITE_CLERK_PUBLISHABLE_KEY: nonEmptyString,
  VITE_CLIENT_URL: httpUrl,
  VITE_DISABLE_SIGNUPS: booleanString,
  VITE_FIREBASE_API_KEY: nonEmptyString,
  VITE_FIREBASE_APP_ID: nonEmptyString,
  VITE_FIREBASE_AUTH_DOMAIN: nonEmptyString,
  VITE_FIREBASE_MEASUREMENT_ID: nonEmptyString,
  VITE_FIREBASE_MESSAGING_SENDER_ID: nonEmptyString,
  VITE_FIREBASE_PROJECT_ID: nonEmptyString,
  VITE_FIREBASE_STORAGE_BUCKET: nonEmptyString,
  // GA4 property "First Round" (G-XXXXXXXXXX). Optional: GA4 stays off until it is set.
  VITE_GA4_MEASUREMENT_ID: z
    .string()
    .trim()
    // eslint-disable-next-line lingui/no-unlocalized-strings -- build-time config error
    .regex(/^G-[A-Z0-9]+$/, "must look like G-XXXXXXXXXX")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  VITE_GOOGLE_PLACES_API_KEY: nonEmptyString,
  VITE_LIVEKIT_WS_URL: wsUrl,
  VITE_LOGODEV_PUBLISHABLE_KEY: nonEmptyString,
  VITE_MIXPANEL_TOKEN: nonEmptyString,
  // "true" lets a local dev server send analytics. Only with a Development project token.
  VITE_ANALYTICS_LOCAL: booleanString.optional(),
  VITE_STRIPE_PUBLISHABLE_KEY: nonEmptyString,
  VITE_SUPPORT_EMAIL: nonEmptyString,
  VITE_SENTRY_DSN: z
    .string()
    .url()
    .refine((value) => value.startsWith("https://"), {
      message: "must be a valid HTTPS URL",
    })
    .optional(),
});

const parsedEnv = clientEnvSchema.safeParse(import.meta.env);

if (!parsedEnv.success) {
  const message = parsedEnv.error.issues
    .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
    .join("; ");

  throw new Error(`Invalid client environment configuration: ${message}`);
}

export const env = Object.freeze({
  ...parsedEnv.data,
  DEV: import.meta.env.DEV,
});
